import React, { useState } from "react";
import Papa from "papaparse";
import { Alert, Badge, FormGroup, Input, Label, ListGroup, ListGroupItem, Progress, Spinner, Table } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { serviceGetContactsByQuery } from "../../../services/contact";

// Row-level and report pieces of the WhatsApp Recovery page (ChatImportWaBackup.js).

const SKIP_REASON_LABELS = {
  UNKNOWN_CONTACT: "Kontak tidak ada di CRM",
  IGNORED: "Diabaikan admin",
};

export const isOk = (response) => new RegExp(/20+/).test(response.status);

export const isMappable = (item) => !item.error && !item.importedChatId;

export const needsMapping = (item) =>
  isMappable(item) && !item.ignored && !item.resolvedContactId && item.match.kind !== "MATCHED";

export const itemState = (item) => {
  if (item.error) return { label: "File tidak valid", color: "danger" };
  if (item.importedChatId) return { label: "Sudah diimport", color: "success" };
  if (item.ignored) return { label: "Diabaikan", color: "light" };
  if (item.resolvedContactId) return { label: "Kontak dipilih", color: "info" };
  if (item.match.kind === "MATCHED") return { label: "Cocok", color: "success" };
  if (item.match.kind === "UNMATCHED_WITH_PHONE") return { label: "Nomor belum di CRM", color: "warning" };
  return { label: "Tanpa nomor", color: "warning" };
};

// Status filters above the table, in the order an admin works through them.
// Every item lands in exactly one filter besides "Semua".
export const FILTERS = [
  {
    key: "UNMATCHED_WITH_PHONE",
    label: "Nomor belum di CRM",
    matches: (item) => needsMapping(item) && item.match.kind === "UNMATCHED_WITH_PHONE",
  },
  {
    key: "UNMATCHED_NO_PHONE",
    label: "Tanpa nomor",
    matches: (item) => needsMapping(item) && item.match.kind === "UNMATCHED_NO_PHONE",
  },
  {
    key: "MATCHED",
    label: "Cocok",
    matches: (item) => isMappable(item) && !item.ignored && !item.resolvedContactId && item.match.kind === "MATCHED",
  },
  {
    key: "RESOLVED",
    label: "Kontak dipilih",
    matches: (item) => isMappable(item) && !item.ignored && Boolean(item.resolvedContactId),
  },
  { key: "IGNORED", label: "Diabaikan", matches: (item) => isMappable(item) && Boolean(item.ignored) },
  { key: "IMPORTED", label: "Sudah diimport", matches: (item) => Boolean(item.importedChatId) },
  { key: "INVALID", label: "Tidak valid", matches: (item) => Boolean(item.error) },
  { key: "ALL", label: "Semua", matches: () => true },
];

export const matchesSearch = (item, query) => {
  if (!query) return true;
  const haystack = `${item.identity?.rawLabel || ""} ${item.filename}`.toLowerCase();
  const digits = query.replace(/\D/g, "");
  // "0812 3456" should still find "+62 812-3456-..." — compare digits only
  // when the query is a number.
  return (
    haystack.includes(query.toLowerCase()) ||
    (digits.length >= 3 && haystack.replace(/\D/g, "").includes(digits.replace(/^0/, "")))
  );
};

const downloadCsv = (rows, filename) => {
  const blob = new Blob([Papa.unparse(rows, { escapeFormulae: true })], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.setAttribute("href", URL.createObjectURL(blob));
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Search existing CRM contacts by name and link the chat to one instead of
// always creating a new contact — avoids a duplicate when this person is
// already in the CRM under a different number.
const ContactSearchAndLink = ({ token, onLink, disabled }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async () => {
    if (!query) return;
    setSearching(true);
    const response = await serviceGetContactsByQuery(token, 1, query);
    setSearching(false);
    setResults(isOk(response) ? response.data?.data || [] : []);
  };

  return (
    <div>
      <div className="d-flex">
        <Input
          type="text"
          placeholder="Cari nama kontak di CRM..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          disabled={disabled}
        />
        <Button color="light" className="ms-2" onClick={handleSearch} disabled={disabled || searching || !query}>
          {searching ? <Spinner size="sm" /> : <Icon name="search" />}
        </Button>
      </div>
      {results.length > 0 && (
        <ListGroup className="mt-2">
          {results.map((contact) => (
            <ListGroupItem key={contact.id} className="d-flex justify-content-between align-items-center">
              <span>
                {contact.name} — {contact.phones?.map((p) => p.phone).join(", ")}
              </span>
              <Button color="primary" size="sm" disabled={disabled} onClick={() => onLink(contact.id)}>
                Link
              </Button>
            </ListGroupItem>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

// Per-row mapping controls, shown when a row is expanded.
export const ItemMappingPanel = ({ item, token, disabled, onResolve }) => {
  const [manualPhone, setManualPhone] = useState("");
  const hasExportPhone = item.match.kind !== "UNMATCHED_NO_PHONE";

  return (
    <div className="p-2">
      <ContactSearchAndLink
        token={token}
        disabled={disabled}
        onLink={(contactId) => onResolve([{ itemKey: item.key, action: "LINK_CONTACT", contactId }])}
      />
      <div className="d-flex align-items-center mt-2">
        {!hasExportPhone && (
          <Input
            type="text"
            placeholder="+62..."
            className="me-2"
            style={{ maxWidth: 200 }}
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            disabled={disabled}
          />
        )}
        <Button
          color="primary"
          size="sm"
          disabled={disabled || (!hasExportPhone && !manualPhone)}
          onClick={() =>
            onResolve([
              { itemKey: item.key, action: "CREATE_CONTACT", phone: hasExportPhone ? undefined : manualPhone },
            ])
          }
        >
          Buat kontak baru{hasExportPhone ? ` (${item.match.phone})` : ""}
        </Button>
        <Button
          color="light"
          size="sm"
          className="ms-2"
          disabled={disabled}
          onClick={() => onResolve([{ itemKey: item.key, action: "IGNORE" }])}
        >
          Abaikan chat ini
        </Button>
      </div>
    </div>
  );
};

const STEPS = ["Upload", "Cek kontak", "Import", "Hasil"];

// Where the admin is in the flow: 0 upload, 1 contact check, 2 importing, 3 result.
export const ImportStepper = ({ current }) => (
  <ol className="wa-stepper">
    {STEPS.map((label, index) => (
      <li key={label} className={index < current ? "is-done" : index === current ? "is-current" : undefined}>
        <span className="wa-stepper__dot">{index < current ? <Icon name="check" /> : index + 1}</span>
        <span className="wa-stepper__label">{label}</span>
      </li>
    ))}
  </ol>
);

const StatCard = ({ value, label, hint, tone, children }) => (
  <div className={`wa-stat wa-stat--${tone}`}>
    <div className="wa-stat__value">{value.toLocaleString("id-ID")}</div>
    <div className="wa-stat__label">{label}</div>
    {hint && <div className="wa-stat__hint">{hint}</div>}
    {children}
  </div>
);

// Before import: what the uploaded files contain and what "Import" will do.
export const ReviewSummary = ({ total, ready, unmatched, invalid, shouldSkipUnknown }) => (
  <div className="wa-stats">
    <StatCard value={total} label="Total chat di file" tone="neutral" />
    <StatCard value={ready} label="Siap diimport" hint="Kontak sudah ada / sudah dipilih" tone="success" />
    <StatCard
      value={unmatched}
      label="Belum ada kontak"
      hint={shouldSkipUnknown ? "Akan dilewati" : "Akan gagal — pilih kontak dulu"}
      tone="warning"
    />
    <StatCard value={invalid} label="Tidak bisa diproses" hint="Duplikat / file tidak valid" tone="muted" />
  </div>
);

// After import: one sentence answer, then the breakdown.
export const ImportResult = ({ report, totalChats }) => {
  const newMessages = report.imported.reduce((sum, entry) => sum + (entry.newMessageCount || 0), 0);
  const handleDownloadSkipped = () =>
    downloadCsv(
      report.skipped.map((entry) => ({
        file: entry.filename,
        kontak: entry.label || "",
        nomor: entry.phone || "",
        alasan: SKIP_REASON_LABELS[entry.reason] || entry.reason,
      })),
      "wa-restore-chat-dilewati.csv",
    );

  return (
    <React.Fragment>
      <Alert color="success" className="wa-result-headline">
        <Icon name="check-circle" />
        <span>
          Import selesai — <strong>{report.imported.length.toLocaleString("id-ID")}</strong> dari{" "}
          {totalChats.toLocaleString("id-ID")} chat berhasil masuk CRM ({newMessages.toLocaleString("id-ID")} pesan
          baru).
        </span>
      </Alert>
      <div className="wa-stats">
        <StatCard value={report.imported.length} label="Berhasil" tone="success" />
        <StatCard value={report.skipped.length} label="Dilewati" hint="Belum ada kontak / diabaikan" tone="warning">
          {report.skipped.length > 0 && (
            <Button color="light" size="sm" className="mt-2" onClick={handleDownloadSkipped}>
              <Icon name="download" />
              <span>Unduh daftar</span>
            </Button>
          )}
        </StatCard>
        <StatCard
          value={report.failed.length}
          label="Tidak bisa diproses"
          hint="Duplikat / file tidak valid"
          tone="muted"
        />
      </div>
    </React.Fragment>
  );
};

// Shown while a confirm runs server-side: live progress, reconnect notice,
// and a resume button once the run looks dead.
export const ImportProgressBanner = ({ progress, isStalled, connectionRetries, canResume, resumeError, onResume }) => (
  <Alert color={isStalled || connectionRetries > 0 ? "warning" : "info"} className="mt-4 wa-import-banner">
    <div className="d-flex align-items-center mb-2">
      {!isStalled && <Spinner size="sm" className="me-2" />}
      <strong>{isStalled ? "Import tampak terhenti" : "Sedang mengimport chat…"}</strong>
      <span className="ms-auto">{progress ? `${progress.processed} / ${progress.total} chat` : "Menyiapkan…"}</span>
    </div>
    <Progress
      animated={!isStalled}
      striped
      color={isStalled || connectionRetries > 0 ? "warning" : "primary"}
      value={progress && progress.total > 0 ? (progress.processed / progress.total) * 100 : 0}
    />
    {isStalled ? (
      <div className="mt-2 small">
        <p className="mb-2">
          Tidak ada kemajuan selama 3 menit — kemungkinan server sempat restart. Chat yang sudah masuk aman dan tidak
          akan diimport dua kali.
          {!canResume && " Pilih Department dan Sales/CS di atas dulu."}
        </p>
        <Button color="warning" size="sm" onClick={onResume} disabled={!canResume}>
          Lanjutkan import
        </Button>
        {resumeError && <div className="text-danger mt-2">{resumeError}</div>}
      </div>
    ) : (
      <p className="mb-0 mt-2 small">
        {connectionRetries > 0
          ? `Koneksi ke server terputus — import tetap berjalan di server. Mencoba terhubung lagi (percobaan ${connectionRetries})…`
          : "Import berjalan di server. Kalau halaman tertutup, buka lagi link halaman ini untuk melihat progresnya."}
      </p>
    )}
  </Alert>
);

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Drag-and-drop / multi-select file picker plus the list of chosen files.
export const UploadDropzone = ({ files, onAdd, onRemove, maxFiles }) => {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <FormGroup>
      <Label>File Backup</Label>
      <label
        className={`wa-upload-zone${isDragging ? " is-dragging" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          onAdd(e.dataTransfer.files);
        }}
      >
        <input
          type="file"
          accept=".txt,.zip"
          multiple
          className="wa-upload-zone__input"
          onChange={(e) => {
            onAdd(e.target.files);
            e.target.value = "";
          }}
        />
        <div className="wa-upload-zone__icon">
          <Icon name="upload-cloud" />
        </div>
        <p className="wa-upload-zone__title">Klik atau seret file ke sini</p>
        <p className="wa-upload-zone__hint">
          Bisa beberapa file sekaligus. Format .txt atau .zip, maksimal {maxFiles} file @ 20MB dan 2.000 chat per upload
        </p>
      </label>

      {files.length > 0 && (
        <ListGroup className="mt-2">
          {files.map((file, index) => (
            <ListGroupItem
              key={`${file.name}-${file.size}`}
              className="d-flex justify-content-between align-items-center"
            >
              <span>
                <Icon name={file.name.toLowerCase().endsWith(".zip") ? "file-zip" : "file-text"} /> {file.name}{" "}
                <span className="text-soft">({formatFileSize(file.size)})</span>
              </span>
              <Button color="light" size="sm" onClick={() => onRemove(index)}>
                <Icon name="cross" />
              </Button>
            </ListGroupItem>
          ))}
        </ListGroup>
      )}
    </FormGroup>
  );
};

// Per-chat rows of the (collapsed by default) detail view.
export const ChatItemsTable = ({
  items,
  selectedKeys,
  selectableKeys,
  rowErrors,
  expandedKey,
  disabled,
  token,
  onToggleAll,
  onToggleSelected,
  onToggleExpanded,
  onResolve,
}) => {
  const isAllSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key));

  return (
    <Table size="sm" responsive className="wa-items-table">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              title={`Pilih semua ${selectableKeys.length} chat di tampilan ini`}
              checked={isAllSelected}
              disabled={disabled || selectableKeys.length === 0}
              onChange={onToggleAll}
            />
          </th>
          <th>Kontak terdeteksi</th>
          <th>Pesan</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && (
          <tr>
            <td colSpan={5} className="text-center text-soft">
              Tidak ada chat yang cocok.
            </td>
          </tr>
        )}
        {items.map((item) => {
          const state = itemState(item);
          const rowError = item.error || rowErrors[item.key];

          return (
            <React.Fragment key={item.key}>
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(item.key)}
                    disabled={disabled || !isMappable(item)}
                    onChange={() => onToggleSelected(item.key)}
                  />
                </td>
                <td title={item.filename}>{item.identity?.rawLabel || item.filename}</td>
                <td>{item.messageCount ?? "-"}</td>
                <td>
                  <Badge color={state.color}>{state.label}</Badge>
                  {rowError && <div className="text-danger small">{rowError}</div>}
                </td>
                <td className="text-end">
                  {isMappable(item) && (
                    <Button color="light" size="sm" disabled={disabled} onClick={() => onToggleExpanded(item.key)}>
                      {expandedKey === item.key ? "Tutup" : "Pilih kontak"}
                    </Button>
                  )}
                </td>
              </tr>
              {expandedKey === item.key && (
                <tr>
                  <td colSpan={5}>
                    <ItemMappingPanel item={item} token={token} disabled={disabled} onResolve={onResolve} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};
